import React, { Component } from 'react'
import { connect } from 'react-redux'
import { push } from 'connected-react-router'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'
import { sortAttributes } from '@sainsburys-tech/boltui-utils'
import negate from 'lodash.negate'

import { by } from '../../utils'

import { Switch, Case, Default } from '../Switch'

import { identifyVariantsWithDifferentAttributesThanProvidedOne, groupByTypeValue } from './AttributeSelectorHelpers'
import { fetchProductStoreRequest } from '../../actionCreators/productStore/productStoreActionCreators'

import * as Styled from './AttributeSelector.styles'

const ColourAndSwatchPicker = loadable(() =>
  import(/* webpackChunkName: "colour-and-swatch-picker" */ './pickers/ColourAndSwatchPicker/ColourAndSwatchPicker')
)
const SizePicker = loadable(() => import(/* webpackChunkName: "size-picker" */ './pickers/SizePicker/SizePicker'))
const ServicePicker = loadable(() =>
  import(/* webpackChunkName: "service-picker" */ './pickers/ServicePicker/ServicePicker')
)
ColourAndSwatchPicker.displayName = 'ColourAndSwatchPicker' // maybe could be remove in version 5.14.3 after https://github.com/gregberge/loadable-components/pull/731/files
SizePicker.displayName = 'SizePicker'
ServicePicker.displayName = 'ServicePicker'

class AttributeSelector extends Component {
  constructor(props) {
    super(props)
    this.state = {
      hasSizeChange: false
    }
  }

  selectPartNumber = (partNumber, changeSize) => () => {
    this.props.push(`/product/${partNumber}`)
    this.props.fetchProduct({ partNumber, quantity: this.props.quantity || 1 })
    if (changeSize) {
      this.setState({ hasSizeChange: true })
    }
  }

  render() {
    const { selectPartNumber } = this

    const {
      sizeGuide,
      partNumber,
      variants,
      sizeInfo,
      breadcrumb,
      categories,
      brand,
      isTuMobileLayout,
      isTuTestVariant
    } = this.props
    // No variants for this product
    if (variants?.length === 0) return null

    return (
      <Styled.Container data-test='attribute-selector'>
        <SelectorList
          {...{
            sizeGuide,
            selectPartNumber,
            partNumber,
            variants,
            sizeInfo,
            hasSizeChange: this.state.hasSizeChange,
            breadcrumb,
            categories,
            brand,
            isTuMobileLayout,
            isTuTestVariant
          }}
        />
      </Styled.Container>
    )
  }
}

AttributeSelector.propTypes = {
  partNumber: PropTypes.string.isRequired,
  variants: PropTypes.array.isRequired,
  sizeInfo: PropTypes.func
}

const SelectorList = props => {
  const {
    sizeGuide,
    selectPartNumber,
    partNumber,
    variants,
    sizeInfo,
    hasSizeChange,
    breadcrumb,
    categories,
    brand,
    isTuMobileLayout,
    isTuTestVariant
  } = props

  const selectedVariant = variants?.find(by.partNumberOf(partNumber))
  if (!selectedVariant) return null // If main variant is missing we remove selector

  const selectorProps = {
    sizeGuide,
    variants,
    partNumber,
    selectPartNumber,
    selectedVariant,
    sizeInfo,
    hasSizeChange,
    breadcrumb,
    categories,
    brand,
    isTuMobileLayout,
    isTuTestVariant
  }

  return selectedVariant.attributes.sort(sortAttributes).map(attributes => {
    const key = `${attributes.type} ${attributes.value}`.replace(/ /g, '-')
    return <Selector {...selectorProps} {...attributes} key={key} />
  })
}

const Selector = props => {
  const {
    sizeGuide,
    variants,
    partNumber,
    selectPartNumber,
    selectedVariant,
    sizeInfo,
    hasSizeChange,
    breadcrumb,
    categories,
    brand,
    isTuMobileLayout,
    isTuTestVariant,
    ...attribute
  } = props

  const { type, value, shortValue } = attribute

  const { attributes } = selectedVariant

  const otherAttributes = attributes.filter(negate(by.attributeOf(attribute)))

  // We flag all the variants that has same type value as selected one but different any other attribute
  const addAvailableInOtherCombination = identifyVariantsWithDifferentAttributesThanProvidedOne(
    variants,
    attribute,
    otherAttributes
  )

  const combinationsGroupedByType = groupByTypeValue(addAvailableInOtherCombination, type, 'value')

  const pickerProps = {
    sizeGuide,
    type,
    value,
    shortValue,
    partNumber,
    combinationsGroupedByType,
    selectPartNumber,
    sizeInfo,
    hasSizeChange,
    breadcrumb,
    categories,
    isTuMobileLayout,
    isTuTestVariant,
    brand
  }
  const selectedFabricName = selectedVariant?.attributes?.find(attr => attr.type === 'Fabric type')?.value

  // Not much to select from
  if (type === 'Colour' && Object.keys(combinationsGroupedByType).length <= 1) return null
  if (type === 'Colour' && selectedFabricName) {
    pickerProps.groupedByFabrics = groupByTypeValue(addAvailableInOtherCombination, 'Fabric type', 'value')
  }
  return (
    <>
      <Styled.SwitchContainer>
        <Switch value={type.toLowerCase()}>
          <Case value='colour'>
            <ColourAndSwatchPicker {...pickerProps} selectedFabricName={selectedFabricName} />
          </Case>
          <Case value='size'>
            <SizePicker {...pickerProps} />
          </Case>
          <Case value='service'>
            <ServicePicker {...pickerProps} />
          </Case>
          <Case value='fabric type'>
            <></>
          </Case>
          <Default>
            <SizePicker {...pickerProps} />
          </Default>
        </Switch>
      </Styled.SwitchContainer>
    </>
  )
}

const mapDispatchToProps = dispatch => {
  const props = {
    push: payload => dispatch(push(payload)),
    fetchProduct: payload => dispatch(fetchProductStoreRequest(payload))
  }
  return props
}

export default connect(null, mapDispatchToProps)(AttributeSelector)
export { Selector, AttributeSelector, mapDispatchToProps }
